import _                                                                                                                   from 'underscore';
import React, { FC }                                                                                                       from 'react';
import classNames                                                                                                          from 'classnames';
import styled                                                                                                              from 'styled-components';
import { UserService }                                                                                                     from '../../../services';
import { stringToColor }                                                                                                   from '../../../util';
import { CorvalAccountLike, FlexAlign, FlexFlow, FlexJustify, SemanticUISize, CorvalUserLike, SystemEvent, CorvalAccount } from '../../../types';
import { Flexbox, FlexboxProps }                                                                                           from '../../containers';
import { Gravatar, GravatarProps }                                                                                         from '../util/visual/Gravatar';
import { Cell, Grid }                                                                                                      from 'styled-css-grid';

interface UserAvatarProps {
	
	/* A user object from graph with at least first, last name */
	user: CorvalAccount | CorvalUserLike | CorvalAccountLike,
	
	/* Generic component or string to accompany the avatar */
	label?: any,
	
	/* Include the user's name or not */
	withName?: boolean,
	
	/* Include the user's email address or not */
	withEmail?: boolean,
	
	/* Display avatar as initials or not */
	asInitials?: boolean,
	
	/* Swapt the horizontal position of the label / name and the avatar */
	reversed?: boolean,
	
	/* Display avatar as circular or not */
	rounded?: boolean,
	
	/* Size of the avatar and accompany label / name */
	size?: SemanticUISize | 'micro' | 'nano' | 'pico',
	
	/* Custom size of the avatar */
	avatarSize?: GravatarProps['size'],
	
	/* Method to handle clicks on the avatar container */
	onClick?( e: SystemEvent ): void,
}

const defaultProps: UserAvatarProps = {
	user:       null,
	withName:   false,
	withEmail:  false,
	label:      null,
	asInitials: false,
	reversed:   false,
	onClick:    null,
	rounded:    true,
	size:       SemanticUISize.MINI,
	avatarSize: null,
}

enum semanticImageSizingMap {
	pico    = 12,
	nano    = 16,
	micro   = 25,
	mini    = 35,
	tiny    = 80,
	small   = 150,
	medium  = 300,
	large   = 450,
	big     = 600,
	huge    = 800,
	massive = 960,
}

const StyledLabel = styled.span<Partial<UserAvatarProps>>`
	display: grid;
	place-content: center;
	font-size: ${ ( props ) => {
	if( props.size === 'micro' ) return `0.8em`;
	if( props.size === 'nano' ) return `0.6em`;
	if( props.size === 'pico' ) return `0.5em`;
	return 'inherit'
} };
	width: ${ ( props ) => props.avatarSize ?? semanticImageSizingMap[ props.size ] }px;
	height: ${ ( props ) => props.avatarSize ?? semanticImageSizingMap[ props.size ] }px;
	background-color: ${ props => stringToColor( UserService.getUserName( props.user ), 65, 50 ) };
	color: white;
	border-radius: ${ props => props.rounded ? '50%' : '0' };
	overflow: hidden;
`;

const StyledGravatar = styled.span<Partial<UserAvatarProps>>`
	display: grid;
	place-content: center;
	width: ${ ( props ) => semanticImageSizingMap[ props.size ] }px;
	height: ${ ( props ) => semanticImageSizingMap[ props.size ] }px;
	border-radius: ${ props => props.rounded ? '50%' : '0' };
	overflow: hidden;
`;

const UserAvatar: FC<UserAvatarProps> = ( props ) => {
	
	if( !props.user ){ return null; }
	
	let { user, avatarSize: customAvatarSize, ...p } = props,
	    avatarSize                                   = semanticImageSizingMap[ props.size ];
	
	let containerProps: FlexboxProps = {
		columns:   `${ ( p.withName || p.withEmail || p.label ) ? `minmax(${ avatarSize }px, auto)` : `auto` } ${ ( p.withName || p.withEmail || p.label ) ? ' 1fr' : '' }`,
		columnGap: '5px',
		onClick:   p.onClick,
		className: p.onClick ? 'cursor--pointer' : null,
		style:     p.reversed ? { direction: 'rtl' } : {}
	};
	
	let nameClasses = classNames( {
		'text-right': p.reversed,
	} );
	
	return (
		<Grid { ...containerProps }>
			<Cell middle>
				{ p.asInitials
				  ? <StyledLabel rounded={ p.rounded } avatarSize={ customAvatarSize } size={ p.size } user={ user }>{ UserService.getUserInitials( user ) }</StyledLabel>
				  : <StyledGravatar rounded={ p.rounded }>
					  <Gravatar size={ customAvatarSize ?? avatarSize } user={ user }/>
				  </StyledGravatar>
				}
			</Cell>
			{ p.withName && <Cell middle className={ nameClasses }>{ UserService.getUserName( user ) }</Cell> }
			{ p.withEmail && <Cell middle className={ nameClasses }>{ UserService.getUserEmail( user as CorvalAccountLike ) }</Cell> }
			{ p.label && <Cell middle className={ nameClasses }>{ p.label }</Cell> }
		
		</Grid>
	)
};

UserAvatar.defaultProps = defaultProps;

export { UserAvatar }
